import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { GripVertical, RotateCcw, Trophy, Timer, Flag } from "lucide-react";

const DEFAULT_DRIVERS = [
    { id: "VER", name: "Max Verstappen", team: "Red Bull" },
    { id: "NOR", name: "Lando Norris", team: "McLaren" },
    { id: "PIA", name: "Oscar Piastri", team: "McLaren" },
    { id: "LEC", name: "Charles Leclerc", team: "Ferrari" },
    { id: "HAM", name: "Lewis Hamilton", team: "Ferrari" },
    { id: "RUS", name: "George Russell", team: "Mercedes" },
    { id: "ANT", name: "Kimi Antonelli", team: "Mercedes" },
    { id: "ALO", name: "Fernando Alonso", team: "Aston Martin" },
    { id: "STR", name: "Lance Stroll", team: "Aston Martin" },
    { id: "ALB", name: "Alex Albon", team: "Williams" },
    { id: "SAI", name: "Carlos Sainz", team: "Williams" },
    { id: "HUL", name: "Nico Hulkenberg", team: "Sauber" },
    { id: "BOR", name: "Gabriel Bortoleto", team: "Sauber" },
    { id: "OCO", name: "Esteban Ocon", team: "Haas" },
    { id: "BEA", name: "Oliver Bearman", team: "Haas" },
    { id: "TSU", name: "Yuki Tsunoda", team: "RB" },
    { id: "LAW", name: "Liam Lawson", team: "RB" },
    { id: "GAS", name: "Pierre Gasly", team: "Alpine" },
    { id: "DOO", name: "Jack Doohan", team: "Alpine" },
    { id: "PER", name: "Sergio Perez", team: "Cadillac" },
];

const SCORING = {
    exact: 8,
    oneAway: 5,
    twoAway: 2,
    qualiExact: 5,
    qualiOneAway: 3,
    qualiTwoAway: 1,
};

function reorder(list, startIndex, endIndex) {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
}

function DriverRow({ driver, index, onDragStart, onDragEnter, onDragEnd, draggingId }) {
    const isDragging = draggingId === driver.id;

    return (
        <motion.div
            layout
            draggable
            onDragStart={(event) => onDragStart(event, index, driver.id)}
            onDragEnter={() => onDragEnter(index)}
            onDragEnd={onDragEnd}
            onDragOver={(event) => event.preventDefault()}
            className={`group grid grid-cols-[44px_1fr_auto] items-center gap-3 rounded-2xl border bg-white p-3 shadow-sm transition cursor-grab active:cursor-grabbing ${isDragging ? "opacity-40 scale-[0.99]" : "hover:shadow-md"
                }`}
        >
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 text-sm font-bold text-slate-700">
                {index + 1}
            </div>
            <div className="min-w-0">
                <div className="truncate font-semibold text-slate-900">{driver.name}</div>
                <div className="text-sm text-slate-500">{driver.team} · {driver.id}</div>
            </div>
            <GripVertical className="h-5 w-5 text-slate-300 group-hover:text-slate-500" />
        </motion.div>
    );
}

function PredictionGrid({ title, icon, drivers, setDrivers, maxRows = 20 }) {
    const [dragIndex, setDragIndex] = useState(null);
    const [draggingId, setDraggingId] = useState(null);

    function handleDragStart(event, index, id) {
        setDragIndex(index);
        setDraggingId(id);
        event.dataTransfer.effectAllowed = "move";
    }

    function handleDragEnter(targetIndex) {
        if (dragIndex === null || dragIndex === targetIndex) return;
        setDrivers((current) => reorder(current, dragIndex, targetIndex));
        setDragIndex(targetIndex);
    }

    function handleDragEnd() {
        setDragIndex(null);
        setDraggingId(null);
    }

    return (
        <div className="rounded-3xl border-slate-200 shadow-sm">
            <div className="p-4 sm:p-5">
                <div className="mb-4 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                        <div className="rounded-2xl bg-slate-900 p-2 text-white">{icon}</div>
                        <div>
                            <h2 className="text-lg font-bold text-slate-900">{title}</h2>
                            <p className="text-sm text-slate-500">Перетащи гонщиков в нужном порядке</p>
                        </div>
                    </div>
                    <div className="rounded-full bg-slate-100 px-3 py-1 text-sm font-medium text-slate-600">
                        Top {maxRows}
                    </div>
                </div>

                <div className="space-y-2">
                    {drivers.slice(0, maxRows).map((driver, index) => (
                        <DriverRow
                            key={driver.id}
                            driver={driver}
                            index={index}
                            draggingId={draggingId}
                            onDragStart={handleDragStart}
                            onDragEnter={handleDragEnter}
                            onDragEnd={handleDragEnd}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}

function calculatePoints(prediction, result, type) {
    return prediction.reduce((total, driver, predictedIndex) => {
        const actualIndex = result.findIndex((item) => item.id === driver.id);
        if (actualIndex === -1) return total;

        const diff = Math.abs(predictedIndex - actualIndex);
        if (type === "quali") {
            if (diff === 0) return total + SCORING.qualiExact;
            if (diff === 1) return total + SCORING.qualiOneAway;
            if (diff === 2) return total + SCORING.qualiTwoAway;
            return total;
        }

        if (diff === 0) return total + SCORING.exact;
        if (diff === 1) return total + SCORING.oneAway;
        if (diff === 2) return total + SCORING.twoAway;
        return total;
    }, 0);
}

export default function F1PredictionGrid() {
    const [qualiPrediction, setQualiPrediction] = useState(DEFAULT_DRIVERS);
    const [racePrediction, setRacePrediction] = useState(DEFAULT_DRIVERS);
    const [submitted, setSubmitted] = useState(false);

    // Demo: actual results are random-ish here. Replace these with real results from your backend.
    const demoActualQuali = useMemo(() => DEFAULT_DRIVERS.slice().sort((a, b) => a.id.localeCompare(b.id)), []);
    const demoActualRace = useMemo(() => DEFAULT_DRIVERS.slice().reverse(), []);

    const qualiPoints = useMemo(
        () => calculatePoints(qualiPrediction.slice(0, 10), demoActualQuali.slice(0, 10), "quali"),
        [qualiPrediction, demoActualQuali]
    );

    const racePoints = useMemo(
        () => calculatePoints(racePrediction.slice(0, 10), demoActualRace.slice(0, 10), "race"),
        [racePrediction, demoActualRace]
    );

    function reset() {
        setQualiPrediction(DEFAULT_DRIVERS);
        setRacePrediction(DEFAULT_DRIVERS);
        setSubmitted(false);
    }

    function exportPrediction() {
        const payload = {
            qualifying: qualiPrediction.map((driver, index) => ({ position: index + 1, driverId: driver.id })),
            race: racePrediction.map((driver, index) => ({ position: index + 1, driverId: driver.id })),
        };

        console.log("Prediction payload", payload);
        setSubmitted(true);
    }

    return (
        <main className="min-h-screen bg-slate-50 p-4 text-slate-900 sm:p-8">
            <div className="mx-auto max-w-7xl">
                <header className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                        <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-slate-900 px-3 py-1 text-sm font-semibold text-white">
                            <Flag className="h-4 w-4" /> F1 Prediction League
                        </div>
                        <h1 className="text-3xl font-black tracking-tight sm:text-5xl">Предикшены на Гран-при</h1>
                        <p className="mt-2 max-w-2xl text-slate-600">
                            Собери порядок гонщиков отдельно для квалификации и гонки. После дедлайна можно сравнить с реальными результатами и начислить очки.
                        </p>
                    </div>

                    <div className="flex gap-2">
                        <button className="rounded-2xl" onClick={reset} >
                            <RotateCcw className="mr-2 h-4 w-4" /> Сбросить

                        </button>
                        <button className="rounded-2xl" onClick={exportPrediction} >
                            Сохранить прогноз

                        </button>
                    </div>
                </header>

                <section className="mb-6 grid gap-3 sm:grid-cols-3">
                    <div className="rounded-3xl border-slate-200 shadow-sm">
                        <div className="p-5">
                            <div className="text-sm text-slate-500">Очки за квалу</div>
                            <div className="mt-1 text-3xl font-black">{submitted ? qualiPoints : "—"}</div>
                            <div className="mt-2 text-xs text-slate-500">Точно: 5 · ±1: 3 · ±2: 1</div>
                        </div>
                    </div>
                    <div className="rounded-3xl border-slate-200 shadow-sm">
                        <div className="p-5">
                            <div className="text-sm text-slate-500">Очки за гонку</div>
                            <div className="mt-1 text-3xl font-black">{submitted ? racePoints : "—"}</div>
                            <div className="mt-2 text-xs text-slate-500">Точно: 8 · ±1: 5 · ±2: 2</div>
                        </div>
                    </div>
                    <div className="rounded-3xl border-slate-200 shadow-sm">
                        <div className="p-5">
                            <div className="text-sm text-slate-500">Итого</div>
                            <div className="mt-1 flex items-center gap-2 text-3xl font-black">
                                <Trophy className="h-7 w-7" /> {submitted ? qualiPoints + racePoints : "—"}
                            </div>
                            <div className="mt-2 text-xs text-slate-500">Демо-подсчёт, замени actual results на реальные</div>
                        </div>
                    </div>
                </section>

                {submitted && (
                    <motion.div
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mb-6 rounded-3xl border border-emerald-200 bg-emerald-50 p-4 text-sm font-medium text-emerald-800"
                    >
                        Прогноз сохранён. Payload выведен в console.log — его можно отправлять на backend.
                    </motion.div>
                )}

                <section className="grid gap-6 lg:grid-cols-2">
                    <PredictionGrid
                        title="Квалификация"
                        icon={<Timer className="h-5 w-5" />}
                        drivers={qualiPrediction}
                        setDrivers={setQualiPrediction}
                        maxRows={20}
                    />
                    <PredictionGrid
                        title="Гонка"
                        icon={<Trophy className="h-5 w-5" />}
                        drivers={racePrediction}
                        setDrivers={setRacePrediction}
                        maxRows={20}
                    />
                </section>
            </div>
        </main>
    );
}
